import {
  CreateDataBlockDictionaryItemDto,
  DataBlockDictionaryColumnDto,
  DataBlockDictionaryDto,
  DataBlockDictionaryItemDto,
  QueryFactory,
} from '@lmt/shared-types';
import { CustomDictionaryName } from '@lmt/unicorn-shared/app/api/custom-hooks';
import { InsuranceCompany } from '@lmt/unicorn-shared/app/components/settings/CustomDictionary/constants';
import { NS_DataBlockFields } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useQueryClient } from '@tanstack/react-query';
import { CellValue, RowValues } from 'exceljs';
import { isEqual } from 'lodash';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Column as ExcelColumn } from 'exceljs';
import { IColumnProps } from 'devextreme-react/cjs/data-grid';

export type ExtendedColumn = IColumnProps & {
  key: string;
  dictionaryId?: number | null;
  isUnique?: boolean;
};

export const useDictionaryKeyTranslation = () => {
  const i18n = useTranslation(['translation', NS_DataBlockFields], {
    nsMode: 'fallback',
  });

  return i18n;
};

export const useInvalidateDependantsHandle = (
  dictionaryId: number | undefined,
) => {
  const { data: dependantsIds } =
    QueryFactory.DataBlockDictionaryQuery.useGetDependantDictionariesQuery(
      {
        dictionaryId,
      },
      { enabled: !!dictionaryId },
    );

  const queryClient = useQueryClient();

  const invalidate = useCallback(
    () =>
      Promise.all(
        dependantsIds?.map(async (x) => {
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.DataBlockDictionaryItemQuery.getQueryKey({
              dictionaryId: x,
            }),
          });
        }) ?? [],
      ),
    [dictionaryId],
  );

  return { invalidate };
};

export function removeEmptyFirstEntries(values: RowValues[]) {
  for (let i = 2; i < values.length; i++) {
    const row = values[i] as CellValue[];
    if (!row) {
      continue;
    }
    if (row[0] === undefined) {
      row.shift();
    }
  }
  return values;
}

function getColumnValuesAsObject(
  item: DataBlockDictionaryItemDto,
  referencedData: Record<string, DataBlockDictionaryItemDto[]>,
) {
  const items = {};
  for (const key in item.values) {
    const dict = referencedData[key];
    if (dict) {
      (items as any)[key] =
        dict.find((x) => x.id === item.values[key])?.value ?? '';
      continue;
    }
    (items as any)[key] = item.values[key];
  }
  return items;
}

/**
 * Export dictionary items to Excel file
 * @param dictionary - Definition of dictionary
 * @param items Dictionary items to export
 * @param referencedData Referenced dictionary items (if column is a dictionary)
 * @param columns
 */
export async function exportToExcelFile(
  dictionary: DataBlockDictionaryDto,
  items: DataBlockDictionaryItemDto[],
  referencedData: Record<string, DataBlockDictionaryItemDto[]>,
  columns: Partial<ExcelColumn>[],
) {
  return import('exceljs').then(async (excelJs) => {
    const workbook = new excelJs.Workbook();
    const worksheet = workbook.addWorksheet('Sheet');
    worksheet.columns = columns;
    const worksheetItems = items.map((x) => {
      return { value: x.value, ...getColumnValuesAsObject(x, referencedData) };
    });
    worksheet.addRows(worksheetItems);

    worksheet.columns.forEach((_column, index) => {
      const cell = worksheet.getCell(1, index + 1);
      cell.font = { bold: true };
      cell.alignment = {
        horizontal: 'center',
      };
    });
    return await workbook.xlsx.writeBuffer();
  });
}

export async function createDictionaryItemObjects(
  dictionaryId: number,
  values: RowValues[],
  columns: ExtendedColumn[],
  referencedData: Record<string, DataBlockDictionaryItemDto[]>,
): Promise<DataBlockDictionaryItemDto[]> {
  const result: DataBlockDictionaryItemDto[] = [];
  for (let i = 2; i < values.length; i++) {
    const row = values[i] as CellValue[];
    try {
      if (!row?.length) {
        continue;
      }

      const dictionaryItem = await mapExcelRowToDictionaryItem(
        row,
        columns,
        referencedData,
      );

      if (!dictionaryItem) {
        continue;
      }
      dictionaryItem.dictionaryId = dictionaryId;
      result.push(dictionaryItem);
    } catch (e) {
      console.error('createDictionaryItemObjects', e);
    }
  }
  return result;
}

async function mapExcelRowToDictionaryItem(
  row: CellValue[],
  columns: ExtendedColumn[],
  referencedData: Record<string, DataBlockDictionaryItemDto[]>,
) {
  const result: any = { value: null, values: {} };
  for (let i = 0; i < columns.length; i++) {
    const column = columns[i];
    let value: string | undefined;
    if (typeof row[i] === 'object') {
      // bug #48788: [object Object] instead of string
      // value may be an object with nested 'richText' property
      value = (row[i] as any)?.richText?.[0]?.text?.trim();
    } else {
      value = row[i]?.toString()?.trim();
    }

    if (column.key === 'value') {
      if (!value) {
        return null;
      }
      result.value = value;
      continue;
    }

    if (!value) {
      continue;
    }

    const dictionary = referencedData[column.key];
    if (dictionary) {
      const dictItem = dictionary.find(
        (item) => item.value.toLowerCase() === value!.toLowerCase(),
      );
      if (dictItem) {
        result.values[column.key] = dictItem.id;
      }
      continue;
    }
    result.values[column.key] = value;
  }
  return result as DataBlockDictionaryItemDto;
}

/**
 * Create DTOs for NEW referenced items (i.e. if column is a dictionary and value doesn't exist yet in this dictionary)
 * @param rowValues
 * @param columns
 * @param referencedData existing referenced items
 */
export async function createReferencedItemObjects(
  rowValues: RowValues[],
  columns: ExtendedColumn[],
  referencedData: Record<string, DataBlockDictionaryItemDto[]>,
): Promise<Record<string, CreateDataBlockDictionaryItemDto[]>> {
  const result: Record<string, CreateDataBlockDictionaryItemDto[]> = {};
  for (let i = 1; i < columns.length; i++) {
    const column = columns[i];
    if (!column.dictionaryId || !column.caption) {
      continue;
    }
    const existingItems = referencedData[column.key];
    const newItems: string[] = [];
    for (let j = 2; j < rowValues.length; j++) {
      const row = rowValues[j] as CellValue[];
      if (!row?.length || !row[0]) {
        // !row is empty or row value (row[0]) is empty, we don't need to import other values (bug #47000)
        continue;
      }
      let value: string | undefined;
      if (typeof row[i] === 'object') {
        value = (row[i] as any)?.richText?.[0]?.text?.trim();
      } else {
        value = row[i]?.toString()?.trim();
      }
      if (
        !value ||
        existingItems.some(
          (item) => item.value.toLowerCase() === value!.toLowerCase(),
        )
      ) {
        continue;
      }
      newItems.push(value);
    }
    if (newItems.length) {
      result[column.caption] = [...new Set(newItems)].map((item) => ({
        value: item,
        dictionaryId: column.dictionaryId!,
      }));
    }
  }
  return result;
}

export function getUniqueColumnsWithDuplicateValues(
  rowValues: RowValues[],
  columns: ExtendedColumn[],
): string[] {
  if (columns.every((x) => !x.isUnique)) {
    return [];
  }
  const result = [];
  for (let i = 0; i < columns.length; i++) {
    const column = columns[i];
    if (!column.isUnique) {
      continue;
    }
    const columnValues: string[] = [];
    for (let j = 2; j < rowValues.length; j++) {
      let row = rowValues[j] as CellValue[];
      if (!row?.length) {
        continue;
      }
      const value = row[i];
      if (value) {
        if (typeof value === 'object') {
          const internalValue = (row[i] as any)?.richText?.[0]?.text?.trim();
          if (internalValue) {
            columnValues.push(internalValue.toLowerCase());
          }
        } else {
          columnValues.push(value.toString().toLowerCase());
        }
      }
    }
    if (new Set(columnValues).size !== columnValues.length) {
      result.push(column.caption!);
    }
  }
  return result;
}

/**
 * Check if columns in file and actual dictionary columns are the same
 * @param values
 * @param dictionaryColumns
 */
export function checkIfColumnsAreTheSame(
  values: RowValues[],
  dictionaryColumns: ExtendedColumn[],
) {
  let columnNamesRow = values[1] as CellValue[];
  if (!columnNamesRow) {
    return false;
  }
  if (columnNamesRow[0] === undefined) {
    columnNamesRow = columnNamesRow.slice(1);
  }
  const fileColumnNames = columnNamesRow.map((x) => x?.toString() ?? '');
  return isEqual(
    fileColumnNames,
    dictionaryColumns.map((x) => x.caption),
  );
}

export function isColumnRequired(
  column: DataBlockDictionaryColumnDto,
  dictionary?: DataBlockDictionaryDto | null,
) {
  if (column.required) {
    return true;
  }
  return (
    dictionary?.title === CustomDictionaryName.InsuranceCompanies &&
    column.title === InsuranceCompany.ShortName
  );
}
